import React from 'react';

import { DialogContentText } from '@mui/material';

import { TranslationKeys } from '../../translations';
import { makeButtonCancelTestId, makeButtonConfirmTestId } from 'app/utils';

import type { SelectedAppState } from '../../store';
import { useStoreSelector } from '../../store';

import ActionButton from '../ActionButton';
import { Dialog } from '../Dialog';

type ConfirmDialogProps = {
  children: React.ReactElement;
  title: React.ReactElement | string;
  open: boolean;
  onClose?: () => void;
  setOpen?: (state: boolean) => void;
  onConfirm?: () => void;
  testId?: string;
};

export const ConfirmDialog = ({ title, children, open, setOpen, onConfirm, onClose, testId }: ConfirmDialogProps) => {
  const { loading: appLoading } = useStoreSelector<SelectedAppState>(state => state.AppReducer);

  const [loading, setLoading] = React.useState(false);

  const closeDialog = React.useCallback(() => {
    onClose && onClose();
    setOpen && setOpen(false);
  }, [onClose, setOpen]);

  const onConfirmButtonClick = React.useCallback(() => {
    setLoading(true);
    onConfirm && onConfirm();
    closeDialog();
    setLoading(false);
  }, [closeDialog, onConfirm]);

  const DialogActions = React.useMemo(() => {
    return (
      <>
        <ActionButton
          messageId={TranslationKeys.no}
          onClick={closeDialog}
          variant="outlined"
          testId={makeButtonCancelTestId(testId)}
        />
        <ActionButton
          messageId={TranslationKeys.yes}
          onClick={onConfirmButtonClick}
          disabled={loading || appLoading}
          testId={makeButtonConfirmTestId(testId)}
        />
      </>
    );
  }, [appLoading, closeDialog, loading, onConfirmButtonClick, testId]);

  return (
    <Dialog open={open} title={title} onClose={closeDialog} actions={DialogActions} testId={testId}>
      <DialogContentText>{children}</DialogContentText>
    </Dialog>
  );
};
